<template>
    <div id="app">
        <h1 class="labelHack">Choir Practice [in Eb Major]</h1>
        <label class="containerLabel labelHack">Oscilloscope</label>
        <div>
            <div class="oscilloscope"></div>
        </div>
        <div id="content">
            <div id="play">
                <div class="key" v-for="(note, index) in activeNotes" @mousedown="playNote(note, index)" @mouseup="endNote(note)" :class="{activeNote: notePlaying == index}">
                    <span class="note">{{note}}</span>
                    <span>{{keys[index]}}</span>
                </div>
            </div>
            <label class="containerLabel">Oscillators</label>
            <div class="configContainer">
                <div>
                    <label class="controlLabel">Base Config</label>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="oscillatorConfig.width" min="0" max="1" step="0.05">
                            <label class="value">{{oscillatorConfig.width}}</label>
                        </div>
                        <label class="units">Pulse Oscillator Width [Normal Range]</label>
                    </div>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="noiseConfig.playbackRate" min="0" max="1" step="0.05">
                            <label class="value">{{noiseConfig.playbackRate}}</label>
                        </div>
                        <label class="units">Noise Playback Rate [Normal Range]</label>
                    </div>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="noiseConfig.volume" min="-20" max="0" step="0.05">
                            <label class="value">{{noiseConfig.volume}}</label>
                        </div>
                        <label class="units">Noise Volume [Decibels]</label>
                    </div>
                </div>
                <div>
                    <label class="controlLabel">Chorus Range</label>
                    <div>
                        <div class="range">
                            <div class="slider">
                                <input type="range" v-model.number="oscillatorRangeConfig.detune" min="0" max="100">
                                <label class="value">{{oscillatorRangeConfig.detune}}</label>
                            </div>
                            <label class="units">Detune [+/- cents]</label>
                        </div>
                    </div>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="oscillatorRangeConfig.phase" min="0" max="50">
                            <label class="value">{{oscillatorRangeConfig.phase}}</label>
                        </div>
                        <label class="units">Phase [+/- degrees]</label>
                    </div>
                </div>
            </div>
            <label class="containerLabel">Formant Filters</label>
            <div class="configContainer">
                <div>
                    <label class="controlLabel">Bass</label>
                    <div div class="formantSelector">
                        <div class="formantButton" @click='updateFormants(5)' :class="{activeFormant: activeFormant == 5}">A</div>
                        <div class="formantButton" @click='updateFormants(6)' :class="{activeFormant: activeFormant == 6}">E</div>
                        <div class="formantButton" @click='updateFormants(7)' :class="{activeFormant: activeFormant == 7}">I</div>
                        <div class="formantButton" @click='updateFormants(8)' :class="{activeFormant: activeFormant == 8}">O</div>
                        <div class="formantButton" @click='updateFormants(9)' :class="{activeFormant: activeFormant == 9}">U</div>
                    </div>
                </div>
                <div>
                    <label class="controlLabel">Soprano</label>
                    <div class="formantSelector">
                        <div class="formantButton" @click='updateFormants(0)' :class="{activeFormant: activeFormant == 0}">A</div>
                        <div class="formantButton" @click='updateFormants(1)' :class="{activeFormant: activeFormant == 1}">E</div>
                        <div class="formantButton" @click='updateFormants(2)' :class="{activeFormant: activeFormant == 2}">I</div>
                        <div class="formantButton" @click='updateFormants(3)' :class="{activeFormant: activeFormant == 3}">O</div>
                        <div class="formantButton" @click='updateFormants(4)' :class="{activeFormant: activeFormant == 4}">U</div>
                    </div>
                </div>
            </div>
            <label class="containerLabel">Envelopes</label>
            <div class="configContainer">
                <div>
                    <label class="controlLabel">Core Envelope</label>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="envelopeBaseConfig.attack" min="0" max="2" step="0.1">
                            <label class="value">{{envelopeBaseConfig.attack}}</label>
                        </div>
                        <label class="units">Attack [seconds]</label>
                    </div>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="envelopeBaseConfig.decay" min="0" max="2" step="0.1">
                            <label class="value">{{envelopeBaseConfig.decay}}</label>
                        </div>
                        <label class="units">Decay [seconds]</label>
                    </div>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="envelopeBaseConfig.sustain" min="0" max="1" step="0.05">
                            <label class="value">{{envelopeBaseConfig.sustain}}</label>
                        </div>
                        <label class="units">Sustain [percent]</label>
                    </div>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="envelopeBaseConfig.release" min="0" max="2" step="0.1">
                            <label class="value">{{envelopeBaseConfig.release}}</label>
                        </div>
                        <label class="units">Release [seconds]</label>
                    </div>
                </div>
                <div>
                    <label class="controlLabel">Envelope Range</label>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="envelopeRangeConfig.attack" min="0" max="0.4" step="0.01">
                            <label class="value">{{envelopeRangeConfig.attack}}</label>
                        </div>
                        <label class="units">Attack Range [+/- seconds]</label>
                    </div>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="envelopeRangeConfig.decay" min="0" max="0.4" step="0.01">
                            <label class="value">{{envelopeRangeConfig.decay}}</label>
                        </div>
                        <label class="units">Decay Range [+/- seconds]</label>
                    </div>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="envelopeRangeConfig.sustain" min="0" max="0.2" step="0.01">
                            <label class="value">{{envelopeRangeConfig.sustain}}</label>
                        </div>
                        <label class="units">Sustain [+/- percent]</label>
                    </div>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="envelopeRangeConfig.release" min="0" max="0.4" step="0.01">
                            <label class="value">{{envelopeRangeConfig.release}}</label>
                        </div>
                        <label class="units">Release Range [+/- seconds]</label>
                    </div>
                </div>
            </div>
            <label class="containerLabel">Effects</label>
            <div class="configContainer">
                <div>
                    <label class="controlLabel">Reverb</label>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="reverbConfig.roomSize" min="0" max="1" step="0.05">
                            <label class="value">{{reverbConfig.roomSize}}</label>
                        </div>
                        <label class="units">Room Size [Normal Range]</label>
                    </div>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="reverbConfig.dampening" min="0" max="3000" step="25">
                            <label class="value">{{reverbConfig.dampening}}</label>
                        </div>
                        <label class="units">Dampening [Frequency]</label>
                    </div>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="reverbConfig.wet" min="0" max="1" step="0.05">
                            <label class="value">{{reverbConfig.wet}}</label>
                        </div>
                        <label class="units">Wet [Normal Range]</label>
                    </div>
                </div>
                <div>
                    <label class="controlLabel">Lowpass Comb Filter</label>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="combFilterConfig.delayTime" min="0" max="1" step="0.05">
                            <label class="value">{{combFilterConfig.delayTime}}</label>
                        </div>
                        <label class="units">Delay Time [Seconds]</label>
                    </div>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="combFilterConfig.resonance" min="0" max="1" step="0.05">
                            <label class="value">{{combFilterConfig.resonance}}</label>
                        </div>
                        <label class="units">Resonance [Normal Range]</label>
                    </div>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="combFilterConfig.wet" min="0" max="1" step="0.05">
                            <label class="value">{{combFilterConfig.wet}}</label>
                        </div>
                        <label class="units">Wet [Normal Range]</label>
                    </div>
                </div>
                <div class="flexHack">
                    <label class="controlLabel">Stereo Widener</label>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="widenerConfig.width" min="0" max="1" step="0.05">
                            <label class="value">{{widenerConfig.width}}</label>
                        </div>
                        <label class="units">Width [Normal Range]</label>
                    </div>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="widenerConfig.wet" min="0" max="1" step="0.05">
                            <label class="value">{{widenerConfig.wet}}</label>
                        </div>
                        <label class="units">Wet [Normal Range]</label>
                    </div>
                </div>
                <div class="flexHack">
                    <label class="controlLabel">EQ3</label>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="eq3Config.low" min="-20" max="0" step="1">
                            <label class="value">{{eq3Config.low}}</label>
                        </div>
                        <label class="units">Low Volume [decibels]</label>
                    </div>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="eq3Config.mid" min="-20" max="0" step="1">
                            <label class="value">{{eq3Config.mid}}</label>
                        </div>
                        <label class="units">Medium Volume [decibels]</label>
                    </div>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="eq3Config.high" min="-20" max="0" step="1">
                            <label class="value">{{eq3Config.high}}</label>
                        </div>
                        <label class="units">High Volume [decibels]</label>
                    </div>
                    <div class="range">
                        <div class="slider">
                            <input type="range" v-model.number="eq3Config.wet" min="0" max="1" step="0.05">
                            <label class="value">{{eq3Config.wet}}</label>
                        </div>
                        <label class="units">Wet [Normal Range]</label>
                    </div>
                </div>
            </div>
            <div id="explanation">
                <p>This is a demo illustrating some of the concepts in <a href="https://medium.com/@brokyo/making-digital-choirs-in-tone-js-f14d84982409" target="_blank">this blog post</a></p>
                <p>A couple note if you're interested in building your own choir</p>
                <ul>
                    <li>The soprano formants don't work quite as well with this oscillator. Try an <a href="https://tonejs.github.io/docs/13.8.25/AMOscillator" target="_blank">AMOscillator</a></li>
                    <li>For performance reasons this choir only has four oscillators. If you're not using a reactive framework you can get at least 50 which will create a much richer sound</li>
                    <li>Performance is also much better when notes are schedule rather than triggered (as they are here)</li>
                    <li>Phasing can lead to some really harsh noises</li>
                    <li>It should be possible to convert this to a proper <a href="https://tonejs.github.io/docs/13.8.25/PolySynth" target="_blank">PolySynth</a> so you can play with harmonies</li>
                </ul>
            </div>
            <img id="logo" src="./assets/as.svg" @click="openSite"></img>
        </div>
    </div>
</template>
<script>
var Tone = require('tone')
var _ = require('lodash')
var formantPresets = require('@/assets/formants.js').formants

export default {
    name: 'app',
    data() {
        return {
            activeFormant: 5,
            bassNotes: ['C2', 'D2', 'Eb2', 'F2', 'G2', 'Ab2', 'Bb2', 'C3', 'D3', 'Eb3'],
            sopranoNotes: ['C4', 'D4', 'Eb4', 'F4', 'G4', 'Ab4', 'Bb4', 'C5', 'D5', 'Eb5'],
            activeNotes: [],
            keys: ['[A]', '[S]', '[D]', '[F]', '[G]', '[H]', '[J]', '[K]', '[L]', '[;]'],
            notePlaying: null,
            oscs: [],
            envelopes: [],
            noteTimerId: null,
            noise: {},
            noiseConfig: {
                playbackRate: 0.7,
                volume: -10
            },
            oscillatorConfig: {
                frequency: 110,
                type: "pulse",
                width: 0.7,
            },
            oscillatorRangeConfig: {
                detune: 0,
                phase: 0
            },
            lfoNodes: [],
            formantNodes: [],
            volumeNodes: [],
            lineOut: {},
            envelopeBaseConfig: {
                attack: 1,
                attackCurve: 'linear',
                decay: 0.7,
                sustain: 0.6,
                release: 1,
                releaseCurve: 'linear'
            },
            envelopeRangeConfig: {
                attack: 0.1,
                decay: 0.1,
                sustain: 0.1,
                release: 0.1,
            },
            reverbConfig: {
                roomSize: 0.7,
                dampening: 3000,
                wet: 1
            },
            reverbNode: {},
            combFilterConfig: {
                delayTime: 0.1,
                resonance: 0.5,
                wet: 1
            },
            combFilterNode: {

            },
            widenerConfig: {
                width: 0.7,
                wet: 1
            },
            widenerNode: {

            },
            eq3Config: {
                low: -4,
                mid: -4,
                high: -6,
                lowFrequency: 400,
                highFrequency: 2500,
                wet: 1
            },
            EQ3Node: {

            }
        }
    },
    methods: {
        openSite() {
            window.open("https://awakening.systems", "_blank");
        },
        changeImage(e) {
            e.path[0].currentSrc = 'foo'
        },
        playNote(note, index) {
            this.notePlaying = index

            this.oscs.forEach(osc =>
                osc.frequency.set({
                    value: Tone.Frequency(note)
                })
            )
            this.envelopes.forEach(envelope => {
                envelope.triggerAttack('+0.01')
            })
        },
        endNote(note) {
            this.notePlaying = null

            this.envelopes.forEach(envelope => {
                envelope.triggerRelease()
            })
        },
        updateFormants(index) {
            if (index > 4) {
                this.activeNotes = this.bassNotes;
            } else {
                this.activeNotes = this.sopranoNotes;
            }

            this.activeFormant = index;
            var vocalizationConfig = formantPresets[index].formants

            vocalizationConfig.forEach((config, index) => {
                this.formantNodes[index].set({
                    frequency: config.frequency,
                    Q: config.frequency / config.bw,
                })

                this.volumeNodes[index].set({
                    volume: config.volume
                })
            })
        },
        createSynthChain(audioCtx) {
            Tone.setContext(audioCtx);

            this.noise = new Tone.Noise(this.noiseConfig).start();

            for (let i = 0; i < 4; i++) {
                let osc = new Tone.OmniOscillator(this.oscillatorConfig).start()
                let envelope = new Tone.AmplitudeEnvelope(this.envelopeBaseConfig)

                osc.connect(envelope)

                this.oscs.push(osc)
                this.envelopes.push(envelope)
            }
            this.noise.connect(this.envelopes[0])


            var lfoConfig = {
                type: 'sine',
                min: 0,
                max: 1,
                phase: 0,
                frequency: '4n',
                amplitude: 1
            }

            // LFOs
            var lfo0 = new Tone.LFO(lfoConfig).start()
            var lfo1 = new Tone.LFO(lfoConfig).start()
            var lfo2 = new Tone.LFO(lfoConfig).start()
            var lfo3 = new Tone.LFO(lfoConfig).start()
            var lfo4 = new Tone.LFO(lfoConfig).start()

            this.lfoNodes = [lfo0, lfo1, lfo2, lfo3, lfo4]

            // Filters
            var universalRolloff = -24
            var f0 = new Tone.Filter({
                type: 'bandpass',
                rolloff: universalRolloff
            })
            var f1 = new Tone.Filter({
                type: 'bandpass',
                rolloff: universalRolloff
            })
            var f2 = new Tone.Filter({
                type: 'bandpass',
                rolloff: universalRolloff
            })
            var f3 = new Tone.Filter({
                type: 'bandpass',
                rolloff: universalRolloff
            })
            var f4 = new Tone.Filter({
                type: 'bandpass',
                rolloff: universalRolloff
            })

            this.formantNodes = [f0, f1, f2, f3, f4]

            // Volume Tuning
            var v0 = new Tone.Volume()
            var v1 = new Tone.Volume()
            var v2 = new Tone.Volume()
            var v3 = new Tone.Volume()
            var v4 = new Tone.Volume()

            this.volumeNodes = [v0, v1, v2, v3, v4]

            this.lineOut = new Tone.Gain(0.7)

            this.lfoNodes.forEach((node, index) => {
                node.connect(this.formantNodes[index].frequency)
            })

            this.formantNodes.forEach((node, index) => {
                this.envelopes[0].chain(node, this.volumeNodes[index], this.lineOut)
            })

            var oscilloscope = new Oscilloscope('.oscilloscope', audioCtx);
            this.lineOut.connect(oscilloscope.analyserNode);
            oscilloscope.start();

            this.updateFormants(5)
            this.reverbNode = new Tone.Freeverb(this.reverbConfig)
            this.combFilterNode = new Tone.FeedbackCombFilter(this.combFilterConfig)
            this.widenerNode = new Tone.StereoWidener(this.widenerConfig)
            this.EQ3Node = new Tone.EQ3(this.eq3Config)

            this.lineOut.chain(this.reverbNode, this.combFilterNode, this.widenerNode, this.EQ3Node, Tone.Master)

        },
        mountPlayButtons() {
            var triggers = ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l', ';']

            triggers.forEach((trigger, index) => {
                window.addEventListener('keydown', e => {
                    if (e.key === trigger & !e.repeat) {
                        let note = this.activeNotes[index]
                        this.playNote(note, index)
                    }
                })
            })

            triggers.forEach(trigger => {
                window.addEventListener('keyup', e => {
                    if (e.key === trigger & !e.repeat) {
                        this.endNote()
                    }
                })
            })
        }
    },
    watch: {
        noiseConfig: {
            deep: true,
            handler(update) {
                this.noise.set(update)
            }
        },
        oscillatorConfig: {
            deep: true,
            handler(update) {
                this.oscs.forEach(osc => {
                    osc.frequency.set({
                        value: update.frequency
                    })

                    osc.set({
                        width: update.width
                    })
                })
            }
        },
        oscillatorRangeConfig: {
            deep: true,
            handler(update) {
                this.oscs.forEach(osc => {
                    osc.set({
                        detune: _.random(update.detune, -update.detune),
                        phase: _.random(update.phase, -update.phase)
                    })
                })
            }
        },
        envelopeBaseConfig: {
            deep: true,
            handler(update) {
                this.envelopes.forEach(envelope => {
                    envelope.set(update)
                })
            }
        },
        envelopeRangeConfig: {
            deep: true,
            handler(update) {
                this.envelopes.forEach(envelope => {

                })
            }
        },
        reverbConfig: {
            deep: true,
            handler(update) {
                this.reverbNode.set(this.reverbConfig)
            }
        },
        combFilterConfig: {
            deep: true,
            handler(update) {
                this.combFilterNode.set(this.combFilterConfig)
            }
        },
        widenerConfig: {
            deep: true,
            handler(update) {
                this.widenerNode.set(this.widenerConfig)
            }
        },
        eq3Config: {
            deep: true,
            handler(update) {
                this.EQ3Node.set(this.eq3Config)
            }
        },
    },
    mounted() {
        var audioCtx = new AudioContext();

        this.createSynthChain(audioCtx)
        this.mountPlayButtons()

        console.warn('if you\'re looking for Tone/Vue best practices you won\'t find them here. This is really a lot of other projects is duct taped together for the sake of a blog post. Feel free to email me if you want to chat about this stuff, though')
    }
}
</script>
<style lang="scss">
#content {
    margin-left: 10px;
}

.labelHack {
    margin-left: 10px;
}

.partialRange {
    display: inline;
}

.activeNote {
    background: pink
}

.verticalRange {
    -webkit-appearance: none;
    height: 150px;
    width: 45px;
    text-align: center;
    border-radius: 5px;
    background: #ccc;
    outline: none;
    writing-mode: bt-lr;
    /* IE */
    -webkit-appearance: slider-vertical;
}

#app {
    position: absolute;
    top: 0;
    left: 0;
    font-family: monospace;
}

.configLabel {
    writing-mode: vertical-rl;
    text-orientation: mixed;
    text-align: center;
    font-size: 22px;
}

.range {
    display: block;
    width: 400px;
    margin-bottom: 4px;
    .slider {
        display: block;
    }
    input[type="range"] {
        width: 80%;
    }
    .value {
        position: relative;
        top: -4px;
    }
    .units {
        font-weight: 100;
        position: relative;
        top: -4px;
        left: 2px;
    }
}

#partials {
    width: 400px;
}

#partial_input {
    display: block;
    width: 20px;
}

.partialLabel {
    text-align: center;
    display: inline-block;
    margin-right: 4px;
    width: 45px;
}

.oscilloscope {
    display: block;
    height: 400px;
    width: 100vw;
}

.oscilloscope__wave {
    fill: none;
    stroke: #000000;
    stroke-width: 2px;
}

.formantContainer {
    display: flex;
}

.formantButton {
    width: 60px;
    height: 40px;
    margin-right: 4px;
    border: 1px solid black;
    text-align: center;
    vertical-align: middle;
}

.configContainer {
    display: flex;
    margin-top: 20px;
    margin-bottom: 40px;
    width: 1000px;
    flex-wrap: wrap;
    label {
        text-align: center;
        font-weight: 900;
    }
    .formantSelector {
        display: flex;
        width: 400px;
        flex-wrap: wrap;
    }
}

#play {
    display: flex;
    justify-content: center;
    margin-bottom: 40px;
    .key {
        width: 60px;
        text-align: center;
        vertical-align: middle;
        height: 40px;
        border: 1px solid black;
        margin-right: 20px;
        .note {
            display: block;
        }
    }
}

.activeFormant {
    background-color: pink;
}

.containerLabel {
    border: 1px solid black;
    padding: 4px;
}

.controlLabel {
    font-size: 16px;
}

.flexHack {
    margin-top: 20px;
}

#logo {
    bottom: 0;
    right: 20px;
    position: fixed;
    height: 75px;
    width: 75px;
}

#logo:hover {
    background: url("./assets/as_text.svg")
}
</style>